import { useState, useEffect } from 'react';
import { getLocalStorageItem, setLocalStorageItem } from '../utils/localStorage';

export interface ToolHistoryEntry<T> {
    id: string;
    timestamp: Date;
    data: T;
    metadata?: Record<string, any>;
}

interface UseToolHistoryOptions {
    maxEntries?: number;
    storageKey: string;
}

interface UseToolHistoryReturn<T> {
    history: ToolHistoryEntry<T>[];
    addToHistory: (data: T, metadata?: Record<string, any>) => void;
    clearHistory: () => void;
    loadFromHistory: (entry: ToolHistoryEntry<T>) => void;
    selectedHistoryId: string | null;
}

/**
 * Reusable hook for managing tool result history
 * Currently uses localStorage but can be easily modified to use database
 */
export function useToolHistory<T>(
    options: UseToolHistoryOptions,
    onHistoryLoad?: (data: T) => void
): UseToolHistoryReturn<T> {
    const { maxEntries = 10, storageKey } = options;
    const [history, setHistory] = useState<ToolHistoryEntry<T>[]>([]);
    const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

    // Load history from localStorage on mount
    useEffect(() => {
        const savedHistory = getLocalStorageItem<ToolHistoryEntry<T>[]>(
            storageKey,
            []
        );
        // Ensure dates are properly parsed
        const parsedHistory = savedHistory.map(entry => ({
            ...entry,
            timestamp: new Date(entry.timestamp)
        }));
        setHistory(parsedHistory);
    }, [storageKey]);

    // Save history to localStorage whenever it changes
    useEffect(() => {
        if (history.length > 0) {
            // Keep only the specified max entries
            const trimmedHistory = history.slice(0, maxEntries);
            setLocalStorageItem(storageKey, trimmedHistory);
        }
    }, [history, storageKey, maxEntries]);

    const addToHistory = (data: T, metadata?: Record<string, any>) => {
        const historyEntry: ToolHistoryEntry<T> = {
            id: Date.now().toString(),
            timestamp: new Date(),
            data,
            metadata
        };

        // Add to history (newest first)
        setHistory(prev => [historyEntry, ...prev]);
        setSelectedHistoryId(historyEntry.id);
    };

    const clearHistory = () => {
        setHistory([]);
        setLocalStorageItem(storageKey, []);
        setSelectedHistoryId(null);
    };

    const loadFromHistory = (entry: ToolHistoryEntry<T>) => {
        setSelectedHistoryId(entry.id);
        if (onHistoryLoad) {
            onHistoryLoad(entry.data);
        }
    };

    return {
        history,
        addToHistory,
        clearHistory,
        loadFromHistory,
        selectedHistoryId
    };
}

// Storage key constants for each tool
export const TOOL_HISTORY_KEYS = {
    INGREDIENT_DETECTOR: 'ingredientDetectionHistory',
    VISION_EXTRACTOR: 'visionExtractorHistory',
    INGREDIENT_PREDICTOR: 'ingredientPredictorHistory'
} as const; 